import { useCallback } from "react";
import { apiFetch } from "@/lib/api/client";
import type { NoticeType, ServerDraft, ServerTarget } from "@/types";
import { createEmptyServerDraft } from "@/lib/formatters/servers";

type Notify = (text: string, type?: NoticeType) => void;
type SetAgentInstallCommand = (command: string | null) => void;

type ServerActionsOptions = {
  localApiOnline: boolean;
  serverDraft: ServerDraft;
  setServerDraft: (draft: ServerDraft) => void;
  setAgentInstallCommand: SetAgentInstallCommand;
  refreshData: () => Promise<unknown>;
  notify: Notify;
};

export function useServerActions({ localApiOnline, serverDraft, setServerDraft, setAgentInstallCommand, refreshData, notify }: ServerActionsOptions) {
  const resetServerDraft = useCallback(() => setServerDraft(createEmptyServerDraft()), [setServerDraft]);

  const editServerTarget = useCallback((server: ServerTarget) => {
    setServerDraft({
      id: server.id,
      name: server.name || "",
      host: server.host || "",
      user: server.user || "root",
      port: String(server.port || "22"),
    });
  }, [setServerDraft]);

  const saveServerTarget = useCallback(async () => {
    if (!localApiOnline) return;
    try {
      const response = await apiFetch("/api/servers", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(serverDraft) });
      const payload = await response.json();
      if (!response.ok || !payload.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      resetServerDraft();
      await refreshData();
      notify(serverDraft.id ? "Сервер обновлён" : "Сервер добавлен");
    } catch (error) {
      notify(`Сервер: ${error instanceof Error ? error.message : String(error)}`, "error");
    }
  }, [localApiOnline, notify, refreshData, resetServerDraft, serverDraft]);

  const waitForHostCommand = useCallback(async (serverId: string, commandId: string) => {
    for (let attempt = 0; attempt < 60; attempt += 1) {
      const response = await apiFetch(`/api/servers/${encodeURIComponent(serverId)}/commands/${encodeURIComponent(commandId)}`);
      const payload = await response.json();
      if (payload.ok && payload.command && ["succeeded", "failed"].includes(payload.command.status)) return payload.command;
      await new Promise((resolve) => setTimeout(resolve, 1000));
    }
    throw new Error("Host Agent не ответил вовремя");
  }, []);

  const serverApiAction = useCallback(async (id: string, action: "test" | "delete" | "install-docker" | "install-agent" | "update-agent" | "uninstall-agent") => {
    try {
      const response = await apiFetch(`/api/servers/${encodeURIComponent(id)}/${action}`, { method: "POST" });
      const payload = await response.json();
      if (!response.ok || !payload.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      if (payload.command?.id) await waitForHostCommand(id, payload.command.id);
      await refreshData();
      notify(payload.warning || `Сервер: ${action} выполнено`, payload.warning ? "error" : "success");
    } catch (error) {
      notify(`Сервер: ${error instanceof Error ? error.message : String(error)}`, "error");
    }
  }, [notify, refreshData, waitForHostCommand]);

  const requestAgentInstall = useCallback(async (id: string) => {
    try {
      const response = await apiFetch(`/api/servers/${encodeURIComponent(id)}/agent-install-command`, { method: "POST" });
      const payload = await response.json();
      if (!response.ok || !payload.ok) throw new Error(payload.error || `HTTP ${response.status}`);
      const command = payload.installCommand || payload.command?.installCommand || payload.command;
      if (typeof command !== "string") throw new Error("Команда установки Host Agent не получена");
      setAgentInstallCommand(command);
    } catch (error) {
      notify(`Host Agent: ${error instanceof Error ? error.message : String(error)}`, "error");
    }
  }, [notify, setAgentInstallCommand]);

  return { saveServerTarget, editServerTarget, resetServerDraft, serverApiAction, requestAgentInstall, waitForHostCommand };
}
